import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { set } from 'lodash'
import * as API from '../../utility/api'
import {
  addFaqRow,
  setModifiedTranslations
} from '../../actions/admin'
import { loadTranslations } from 'react-redux-i18n'

const LANG_LABELS = {
  fi: 'Suomi',
  en: 'English',
  sv: 'Svenska'
}

const SKIP_LANGS = {
  expires: true
}

const HIDDEN_LANGS = {
  errors: true,
  expires: true
}

const isNestedGroup = value =>
  value != null && typeof value === 'object' && !Array.isArray(value)

const withoutExpires = translations => {
  if (!translations || typeof translations !== 'object' || Array.isArray(translations)) {
    return {}
  }

  const next = {}
  Object.keys(translations).forEach(key => {
    if (SKIP_LANGS[key]) return
    const value = translations[key]
    if (isNestedGroup(value)) {
      next[key] = value
    }
  })
  return next
}

const bundleHasLanguages = translations => {
  const langs = withoutExpires(translations)
  return Object.keys(langs).some(lang => {
    if (HIDDEN_LANGS[lang]) return false
    const leaves = new Map()
    collectLeaves(langs[lang], [], leaves)
    return leaves.size > 0
  })
}

const collectLeaves = (node, path, into) => {
  if (!isNestedGroup(node)) return
  Object.keys(node).forEach(key => {
    const nextPath = path.concat(key)
    const value = node[key]
    if (isNestedGroup(value)) {
      collectLeaves(value, nextPath, into)
    } else {
      into.set(nextPath.join('.'), nextPath)
    }
  })
}

const valueAt = (node, path) => {
  let current = node
  for (let i = 0; i < path.length; i += 1) {
    if (current == null || typeof current !== 'object') return ''
    current = current[path[i]]
  }
  if (current == null) return ''
  return String(current)
}

const splitFullKey = fullKey => {
  const parts = String(fullKey || '')
    .split('.')
    .map(part => part.trim())
    .filter(Boolean)
  const key = parts[parts.length - 1]
  const topic = parts[parts.length - 2]
  const parentPath = parts.slice(0, -2)
  return { parts, key, topic, parentPath }
}

const canCreateLeaf = (root, parts) => {
  let current = root
  for (let i = 0; i < parts.length; i += 1) {
    if (current == null) return true
    if (!isNestedGroup(current)) return false
    if (!Object.prototype.hasOwnProperty.call(current, parts[i])) return true
    current = current[parts[i]]
  }
  return false
}

const overlayValue = (pendingMap, translations, lang, path) => {
  const fullKey = path.join('.')
  const buffered = pendingMap && pendingMap[`${lang}:${fullKey}`]
  if (buffered) return buffered.value == null ? '' : String(buffered.value)
  return valueAt(translations && translations[lang], path)
}

const TranslationField = ({ lang, fullKey, value, missing, onCommit }) => {
  const [text, setText] = useState(value || '')
  const sent = useRef(value || '')

  useEffect(() => {
    const next = value == null ? '' : String(value)
    if (next !== sent.current) {
      setText(next)
      sent.current = next
    }
  }, [value])

  const rows = Math.min(8, Math.max(2, text.split('\n').length + 1))

  return (
    <textarea
      aria-label={`${LANG_LABELS[lang] || lang}: ${fullKey}`}
      className={missing && !text.trim() ? 'is-missing' : undefined}
      rows={rows}
      value={text}
      onChange={event => {
        const next = event.target.value
        setText(next)
        sent.current = next
        onCommit(lang, fullKey, next)
      }}
    />
  )
}

const Translations = ({
  modifiedTranslations,
  i18nTranslations,
  dispatchAddFaqRow,
  dispatchLoadTranslations,
  ensureTranslations,
  replaceTranslations
}) => {
  const [query, setQuery] = useState('')
  const [topic, setTopic] = useState('')
  const didInitTopic = useRef(false)
  const pending = useRef({})
  const editGenRef = useRef(0)
  const [dirty, setDirty] = useState(false)
  const [editGen, setEditGen] = useState(0)
  const [addError, setAddError] = useState(null)
  const [fetching, setFetching] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [saveOk, setSaveOk] = useState(false)
  const [adding, setAdding] = useState(false)
  const [newKey, setNewKey] = useState('')
  const [newValues, setNewValues] = useState({})

  const translations = useMemo(
    () => withoutExpires(modifiedTranslations),
    [modifiedTranslations]
  )

  useEffect(() => {
    if (bundleHasLanguages(modifiedTranslations)) return
    if (!bundleHasLanguages(i18nTranslations)) return
    ensureTranslations()
  }, [modifiedTranslations, i18nTranslations, ensureTranslations])

  const languages = useMemo(() => {
    const keys = Object.keys(translations || {}).filter(lang => !HIDDEN_LANGS[lang])
    const preferred = ['fi', 'en'].filter(lang => keys.indexOf(lang) !== -1)
    const rest = keys.filter(lang => preferred.indexOf(lang) === -1).sort()
    return preferred.concat(rest)
  }, [translations])

  const rows = useMemo(() => {
    const leaves = new Map()
    languages.forEach(lang => collectLeaves(translations[lang], [], leaves))
    return Array.from(leaves.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([fullKey, path]) => ({
        fullKey,
        path,
        group: path[0],
        label: path.slice(1).join('.') || path[0]
      }))
  }, [languages, translations])

  const topics = useMemo(() => {
    const pendingMap = editGen >= 0 ? pending.current || {} : {}
    const counts = {}
    const missing = {}
    rows.forEach(row => {
      counts[row.group] = (counts[row.group] || 0) + 1
      const incomplete = languages.some(
        lang => !overlayValue(pendingMap, translations, lang, row.path).trim()
      )
      if (incomplete) {
        missing[row.group] = (missing[row.group] || 0) + 1
      }
    })
    return Object.keys(counts)
      .sort()
      .map(name => ({
        name,
        count: counts[name],
        missing: missing[name] || 0
      }))
  }, [editGen, languages, rows, translations])

  useEffect(() => {
    if (!didInitTopic.current && topics.length) {
      didInitTopic.current = true
      setTopic(topics[0].name)
    }
  }, [topics])

  const filteredRows = useMemo(() => {
    const pendingMap = editGen >= 0 ? pending.current || {} : {}
    const needle = query.trim().toLowerCase()
    return rows.filter(row => {
      if (topic && row.group !== topic) return false
      if (!needle) return true
      if (row.fullKey.toLowerCase().indexOf(needle) !== -1) return true
      return languages.some(lang =>
        overlayValue(pendingMap, translations, lang, row.path)
          .toLowerCase()
          .includes(needle)
      )
    })
  }, [editGen, languages, query, rows, topic, translations])

  const missingTotal = useMemo(() => {
    const pendingMap = editGen >= 0 ? pending.current || {} : {}
    return rows.filter(row =>
      languages.some(
        lang => !overlayValue(pendingMap, translations, lang, row.path).trim()
      )
    ).length
  }, [editGen, languages, rows, translations])

  const mergePending = useCallback(bundle => {
    const source = isNestedGroup(bundle) ? bundle : {}
    const parsed = JSON.parse(JSON.stringify(source))
    const next = isNestedGroup(parsed) ? parsed : {}
    Object.keys(pending.current || {}).forEach(id => {
      const item = pending.current[id]
      if (!item) return
      const { key, topic: topicName, parentPath } = splitFullKey(item.fullKey)
      if (!key || !topicName) return
      if (!isNestedGroup(next[item.lang])) next[item.lang] = {}
      const pathString = parentPath.length
        ? `${parentPath.join('.')}.${topicName}.${key}`
        : `${topicName}.${key}`
      set(next[item.lang], pathString, item.value)
    })
    return next
  }, [])

  const applyPending = useCallback(
    bundle => {
      const next = mergePending(bundle)
      pending.current = {}
      return next
    },
    [mergePending]
  )

  const markDirty = useCallback(() => {
    editGenRef.current += 1
    setEditGen(editGenRef.current)
    setDirty(true)
    setSaveOk(false)
  }, [])

  const commit = useCallback(
    (lang, fullKey, value) => {
      pending.current[`${lang}:${fullKey}`] = { lang, fullKey, value }
      markDirty()
    },
    [markDirty]
  )

  const saveTranslations = () => {
    if (!bundleHasLanguages(translations)) {
      setSaveError('Käännöksiä ei ole ladattu.')
      return
    }

    const saveGen = editGenRef.current
    const payload = applyPending(translations)
    replaceTranslations(payload)
    dispatchLoadTranslations(payload)
    setFetching(true)
    setSaveError(null)
    setSaveOk(false)

    API.POST('admin/translations', {
      modifiedTranslations: payload
    })
      .then(() => {
        setFetching(false)
        if (editGenRef.current !== saveGen) return
        setDirty(false)
        setSaveOk(true)
      })
      .catch(() => {
        setFetching(false)
        setSaveError('Tallennus epäonnistui.')
      })
  }

  const handleAddKey = event => {
    event.preventDefault()
    const { key, topic: topicName, parts } = splitFullKey(newKey)
    if (!key || !topicName || parts.length < 2) return

    const payload = mergePending(translations)
    const collision = languages.some(lang => !canCreateLeaf(payload[lang], parts))
    if (collision) {
      setAddError('Avain on jo käytössä tai törmää olemassa olevaan ryhmään.')
      return
    }

    pending.current = {}
    languages.forEach(lang => {
      if (!payload[lang]) payload[lang] = {}
      set(payload[lang], parts.join('.'), newValues[lang] || '')
    })
    replaceTranslations(payload)
    markDirty()
    setAddError(null)
    setTopic(parts[0])
    setQuery('')
    setNewKey('')
    setNewValues({})
    setAdding(false)
  }

  const handleAddFaq = () => {
    replaceTranslations(applyPending(translations))
    dispatchAddFaqRow()
    markDirty()
    setTopic('faq')
  }

  const newKeyValid = splitFullKey(newKey).parts.length >= 2

  if (!modifiedTranslations) {
    return <p className="muted">Ladataan käännöksiä…</p>
  }

  return (
    <div className="i18n-editor">
      <div className="i18n-editor__toolbar">
        <div className="i18n-editor__search">
          <label htmlFor="i18n-search">Haku</label>
          <input
            id="i18n-search"
            type="search"
            placeholder="Avain tai teksti…"
            value={query}
            onChange={event => {
              const next = event.target.value
              setQuery(next)
              if (next.trim()) setTopic('')
            }}
          />
        </div>

        <p className="i18n-editor__meta">
          {rows.length} avainta
          {missingTotal ? ` · ${missingTotal} puutteellista` : ''}
          {dirty ? ' · tallentamattomia muutoksia' : ''}
        </p>

        <div className="i18n-editor__actions">
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => setAdding(open => !open)}
          >
            {adding ? 'Peruuta' : 'Uusi avain'}
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={handleAddFaq}
          >
            Lisää FAQ-rivi
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={fetching || !dirty || !rows.length}
            onClick={saveTranslations}
          >
            {fetching ? 'Tallennetaan…' : 'Tallenna'}
          </button>
        </div>
      </div>

      {saveError && (
        <div className="notice notice--warning">
          <p>{saveError}</p>
        </div>
      )}
      {saveOk && !dirty && (
        <div className="notice notice--success">
          <p>Käännökset tallennettu.</p>
        </div>
      )}

      {adding && (
        <form className="i18n-editor__add" onSubmit={handleAddKey}>
          <div className="field">
            <label htmlFor="i18n-new-key">Avain</label>
            <input
              id="i18n-new-key"
              type="text"
              placeholder="esim. user.greeting"
              value={newKey}
              onChange={event => {
                setNewKey(event.target.value)
                setAddError(null)
              }}
            />
            <p className="field__help">
              Vähintään aihe ja avain pisteellä erotettuna. Uusi aihe syntyy
              automaattisesti.
            </p>
            {addError && <p className="field__error">{addError}</p>}
          </div>
          <div className="i18n-editor__add-langs">
            {languages.map(lang => (
              <div className="field" key={lang}>
                <label htmlFor={`i18n-new-${lang}`}>
                  {LANG_LABELS[lang] || lang}
                </label>
                <textarea
                  id={`i18n-new-${lang}`}
                  rows={3}
                  value={newValues[lang] || ''}
                  onChange={event =>
                    setNewValues(prev => ({
                      ...prev,
                      [lang]: event.target.value
                    }))
                  }
                />
              </div>
            ))}
          </div>
          <button
            type="submit"
            className="btn btn--secondary btn--sm"
            disabled={!newKeyValid}
          >
            Lisää avain
          </button>
        </form>
      )}

      <div className="i18n-editor__body">
        <nav className="i18n-editor__topics" aria-label="Käännösaiheet">
          <button
            type="button"
            className={'i18n-editor__topic' + (!topic ? ' is-active' : '')}
            onClick={() => setTopic('')}
          >
            <span>Kaikki</span>
            <span className="i18n-editor__count">{rows.length}</span>
          </button>
          {topics.map(item => (
            <button
              type="button"
              key={item.name}
              className={
                'i18n-editor__topic' + (topic === item.name ? ' is-active' : '')
              }
              onClick={() => {
                setTopic(item.name)
                setQuery('')
              }}
            >
              <span>{item.name}</span>
              <span className="i18n-editor__count">
                {item.missing
                  ? `${item.count} · ${item.missing} vajaata`
                  : item.count}
              </span>
            </button>
          ))}
        </nav>

        <div className="i18n-editor__sheet">
          <div
            className="i18n-editor__head"
            style={{
              gridTemplateColumns: `minmax(140px, 0.28fr) repeat(${languages.length}, minmax(0, 1fr))`
            }}
          >
            <div>Avain</div>
            {languages.map(lang => (
              <div key={lang}>{LANG_LABELS[lang] || lang}</div>
            ))}
          </div>

          {filteredRows.length === 0 && (
            <p className="i18n-editor__empty">Ei osumia.</p>
          )}

          {filteredRows.map(row => (
            <div
              key={row.fullKey}
              className="i18n-editor__row"
              style={{
                gridTemplateColumns: `minmax(140px, 0.28fr) repeat(${languages.length}, minmax(0, 1fr))`
              }}
            >
              <div className="i18n-editor__key">
                <code>{row.label}</code>
                {(!topic || row.path.length > 2) && (
                  <span className="i18n-editor__fullkey">{row.fullKey}</span>
                )}
              </div>
              {languages.map(lang => {
                const current = overlayValue(
                  pending.current,
                  translations,
                  lang,
                  row.path
                )
                return (
                  <TranslationField
                    key={lang}
                    lang={lang}
                    fullKey={row.fullKey}
                    value={current}
                    missing={!current.trim()}
                    onCommit={commit}
                  />
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = state => ({
  modifiedTranslations: state.admin.modifiedTranslations,
  i18nTranslations: state.i18n && state.i18n.translations
})

const mapDispatchToProps = dispatch => ({
  ensureTranslations: () => dispatch(setModifiedTranslations()),
  replaceTranslations: bundle => dispatch(setModifiedTranslations(bundle)),
  dispatchAddFaqRow: () => dispatch(addFaqRow()),
  dispatchLoadTranslations: translations =>
    dispatch(loadTranslations(translations))
})

export default connect(mapStateToProps, mapDispatchToProps)(Translations)
